import { useEffect, useState } from 'react';
import {
  Badge, Box, Button, Card, Center, Flex, Heading, HStack, Image, Input, Link, Spinner,
  Table, Tbody, Td, Text, Th, Thead, Tr, VStack, Wrap, WrapItem,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  useDisclosure, useToast, type ImageProps,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdBrokenImage } from 'react-icons/md';
import {
  useCustomerRequests, useCustomerRequest, useUpdateCustomerRequest,
  type SupportRequestStatus,
} from '../../api/customerRequestsApi';
import { apiClient } from '../../services/apiClient';
import { DataTablePagination } from '../../components/DataTable';

const PAGE_SIZE = 20;

function statusColor(status: SupportRequestStatus): string {
  switch (status) {
    case 'new': return 'orange';
    case 'resolved': return 'green';
    case 'archived': return 'gray';
    default: return 'gray';
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function CustomerRequestsListPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>('new');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const detail = useDisclosure();

  const { data, isLoading } = useCustomerRequests({ status, search, page, pageSize: PAGE_SIZE });
  const requestDetail = useCustomerRequest(detail.isOpen ? selectedId : null);
  const updateMutation = useUpdateCustomerRequest();
  const bigImage = useAuthObjectUrl(lightbox?.url ?? null);

  const items = data?.items ?? [];

  const openDetail = (id: string) => {
    setSelectedId(id);
    detail.onOpen();
  };

  const changeStatus = async (id: string, next: SupportRequestStatus, closeAfter = false) => {
    try {
      await updateMutation.mutateAsync({ id, status: next });
      toast({ title: t('admin.customerRequests.updated'), status: 'success', duration: 3000 });
      // Invalidate the list and the specific request that was updated (which may
      // differ from the one open in the modal when acting from a table row).
      queryClient.invalidateQueries({ queryKey: ['admin-customer-requests'] });
      queryClient.invalidateQueries({ queryKey: ['admin-customer-request', id] });
      if (closeAfter) detail.onClose();
    } catch {
      toast({ title: t('admin.customerRequests.updateFailed'), status: 'error', duration: 5000 });
    }
  };

  const filters: Array<{ key: string; label: string }> = [
    { key: 'new', label: t('admin.customerRequests.status.new') },
    { key: 'resolved', label: t('admin.customerRequests.status.resolved') },
    { key: 'archived', label: t('admin.customerRequests.status.archived') },
    { key: '', label: t('admin.customerRequests.status.all') },
  ];

  return (
    <Box>
      <Heading size="lg" mb={1}>{t('admin.customerRequests.title')}</Heading>
      <Text color="gray.500" mb={4}>{t('admin.customerRequests.subtitle')}</Text>

      <Card p={4} mb={4}>
        <Flex gap={3} wrap="wrap" align="center">
          <HStack>
            {filters.map(f => (
              <Button
                key={f.key || 'all'}
                size="sm"
                variant={status === f.key ? 'solid' : 'outline'}
                colorScheme={status === f.key ? 'brand' : 'gray'}
                onClick={() => { setStatus(f.key); setPage(1); }}
                data-testid={`filter-${f.key || 'all'}`}
              >
                {f.label}
              </Button>
            ))}
          </HStack>
          <Input
            placeholder={t('admin.customerRequests.searchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            maxW="320px"
            size="sm"
            data-testid="customer-requests-search"
          />
        </Flex>
      </Card>

      <Card>
        {isLoading ? (
          <Flex justify="center" p={10}><Spinner /></Flex>
        ) : items.length === 0 ? (
          <Flex justify="center" p={10}><Text color="gray.500">{t('admin.customerRequests.empty')}</Text></Flex>
        ) : (
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>{t('admin.customerRequests.col.date')}</Th>
                  <Th>{t('admin.customerRequests.col.organization')}</Th>
                  <Th>{t('admin.customerRequests.col.email')}</Th>
                  <Th>{t('admin.customerRequests.col.category')}</Th>
                  <Th>{t('admin.customerRequests.col.status')}</Th>
                  <Th>{t('admin.customerRequests.col.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map(r => (
                  <Tr key={r.id} data-testid={`customer-request-row-${r.id}`}>
                    <Td whiteSpace="nowrap">{formatDate(r.createdAt)}</Td>
                    <Td>
                      {r.organizationName ? (
                        <Link as={RouterLink} to={`/organizations/${r.organizationId}`} color="brand.500" fontWeight="500">
                          {r.organizationName}
                        </Link>
                      ) : '—'}
                    </Td>
                    <Td>{r.contactEmail}</Td>
                    <Td>
                      <Badge>{t(`admin.customerRequests.category.${r.category}`, r.category)}</Badge>
                      {r.attachmentCount > 0 && (
                        <Badge ml={1} colorScheme="purple">📎 {r.attachmentCount}</Badge>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={statusColor(r.status)}>
                        {t(`admin.customerRequests.status.${r.status}`)}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Button size="xs" variant="outline" onClick={() => openDetail(r.id)} data-testid={`view-${r.id}`}>
                          {t('admin.customerRequests.view')}
                        </Button>
                        {r.status === 'new' && (
                          <Button size="xs" colorScheme="green" variant="outline"
                            onClick={() => changeStatus(r.id, 'resolved')}
                            isLoading={updateMutation.isPending}
                            data-testid={`resolve-${r.id}`}>
                            {t('admin.customerRequests.resolve')}
                          </Button>
                        )}
                        {r.status !== 'archived' && (
                          <Button size="xs" variant="ghost"
                            onClick={() => changeStatus(r.id, 'archived')}
                            data-testid={`archive-${r.id}`}>
                            {t('admin.customerRequests.archive')}
                          </Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {data && (
          <DataTablePagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={data.totalCount}
            totalPages={Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE))}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Detail modal */}
      <Modal isOpen={detail.isOpen} onClose={detail.onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('admin.customerRequests.detailTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {requestDetail.isLoading || !requestDetail.data ? (
              <Flex justify="center" p={6}><Spinner /></Flex>
            ) : (
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.500">{t('admin.customerRequests.col.organization')}</Text>
                  {requestDetail.data.organizationName ? (
                    <Link as={RouterLink} to={`/organizations/${requestDetail.data.organizationId}`} color="brand.500" fontSize="sm" fontWeight="500">
                      {requestDetail.data.organizationName}
                    </Link>
                  ) : (
                    <Text fontSize="sm" fontWeight="500">—</Text>
                  )}
                </HStack>
                <Field label={t('admin.customerRequests.col.email')} value={requestDetail.data.contactEmail} />
                <Field label={t('admin.customerRequests.col.category')}
                  value={t(`admin.customerRequests.category.${requestDetail.data.category}`, requestDetail.data.category)} />
                <Field label={t('admin.customerRequests.marketing')}
                  value={requestDetail.data.marketingOptIn ? t('admin.customerRequests.yes') : t('admin.customerRequests.no')} />
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.500">{t('admin.customerRequests.col.message')}</Text>
                  <Text whiteSpace="pre-wrap">{requestDetail.data.message}</Text>
                </Box>
                {requestDetail.data.attachments.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.500" mb={2}>
                      {t('admin.customerRequests.attachments')}
                    </Text>
                    <Wrap>
                      {requestDetail.data.attachments.map(a => (
                        <WrapItem key={a.id}>
                          <AttachmentImage
                            path={a.url}
                            alt={a.fileName}
                            boxSize="96px"
                            objectFit="cover"
                            borderRadius="md"
                            cursor="zoom-in"
                            _hover={{ opacity: 0.85 }}
                            onClick={() => setLightbox({ url: a.url, name: a.fileName })}
                            data-testid={`attachment-thumb-${a.id}`}
                          />
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            {requestDetail.data && (
              <>
                {requestDetail.data.status !== 'new' && (
                  <Button variant="ghost" onClick={() => changeStatus(requestDetail.data!.id, 'new', true)}>
                    {t('admin.customerRequests.reopen')}
                  </Button>
                )}
                {requestDetail.data.status === 'new' && (
                  <Button colorScheme="green" onClick={() => changeStatus(requestDetail.data!.id, 'resolved', true)}>
                    {t('admin.customerRequests.resolve')}
                  </Button>
                )}
                {requestDetail.data.status !== 'archived' && (
                  <Button variant="outline" onClick={() => changeStatus(requestDetail.data!.id, 'archived', true)}>
                    {t('admin.customerRequests.archive')}
                  </Button>
                )}
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Attachment lightbox — full-size image over a dark backdrop */}
      <Modal isOpen={!!lightbox} onClose={() => setLightbox(null)} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="fit-content">
          <ModalCloseButton color="white" bg="blackAlpha.500" _hover={{ bg: 'blackAlpha.700' }} />
          <ModalBody display="flex" flexDirection="column" alignItems="center" gap={3} p={0}>
            {lightbox && (
              bigImage.error ? (
                <Center boxSize="320px" color="whiteAlpha.800" flexDirection="column" gap={2}>
                  <MdBrokenImage size={40} />
                  <Text fontSize="sm">{lightbox.name}</Text>
                </Center>
              ) : !bigImage.url ? (
                <Center boxSize="320px"><Spinner color="white" /></Center>
              ) : (
                <>
                  <Image
                    src={bigImage.url}
                    alt={lightbox.name}
                    maxH="82vh"
                    maxW="90vw"
                    objectFit="contain"
                    borderRadius="md"
                    data-testid="attachment-lightbox-image"
                  />
                  <HStack color="whiteAlpha.900" fontSize="sm" spacing={3}>
                    <Text>{lightbox.name}</Text>
                    <Link href={bigImage.url} download={lightbox.name} textDecoration="underline">
                      {t('admin.customerRequests.openOriginal')}
                    </Link>
                  </HStack>
                </>
              )
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

/**
 * Fetches a private admin resource (support attachment) with the bearer token and
 * exposes it as a blob object URL — an <img src> can't send the Authorization header,
 * and the object-storage host isn't browser-reachable, so we proxy through the admin API.
 */
function useAuthObjectUrl(path: string | null): { url: string | null; error: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setError(false);
      return;
    }
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(false);
    apiClient
      .getBlob(path)
      .then(blob => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return { url, error };
}

/** An <Image> whose source is a private, auth-proxied attachment. */
function AttachmentImage({ path, ...imgProps }: { path: string } & ImageProps) {
  const { url, error } = useAuthObjectUrl(path);
  if (error) {
    return (
      <Center boxSize="96px" borderWidth="1px" borderColor="gray.200" borderRadius="md" color="gray.400">
        <MdBrokenImage size={24} />
      </Center>
    );
  }
  if (!url) {
    return (
      <Center boxSize="96px">
        <Spinner size="sm" />
      </Center>
    );
  }
  return <Image src={url} {...imgProps} />;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.500">{label}</Text>
      <Text fontSize="sm" fontWeight="500">{value}</Text>
    </HStack>
  );
}
